import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import './App.css';
import FeedbackScreen from './components/FeedbackScreen';
import ThankYouScreen from './components/ThankYouScreen';
import ContactFormScreen from './components/ContactFormScreen';
import QRCodeScreen from './components/QRCodeScreen';
import LocationLogin from './components/LocationLogin';
import MobileLogin from './components/MobileLogin';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
// PWA components removed - no longer needed
import FullscreenButton from './components/FullscreenButton';

function App() {
  const [appMode, setAppMode] = useState('loading'); // 'loading', 'locationLogin', 'feedback', 'admin', 'mobileLogin'
  const [currentScreen, setCurrentScreen] = useState('feedback');
  const [selectedRating, setSelectedRating] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [sessionCheckStatus, setSessionCheckStatus] = useState('idle'); // 'idle', 'checking', 'expired'

  // Add admin-mode class to body for CSS overrides when in admin mode
  useEffect(() => {
    if (appMode === 'admin') {
      document.body.classList.add('admin-mode');
    } else {
      document.body.classList.remove('admin-mode');
    }
    
    return () => {
      document.body.classList.remove('admin-mode');
    };
  }, [appMode]);

  // Initialize app and determine mode based on URL
  useEffect(() => {
    const path = window.location.pathname;
    
    if (path === '/admin') {
      setAppMode('admin');
      checkExistingAdminSession();
    } else if (path === '/mobile-login') {
      setAppMode('mobileLogin');
    } else {
      // Check if there's an existing session for this device
      checkExistingSession();
    }
  }, []);

  const checkExistingSession = async () => {
    try {
      // Ensure we have a stable device_id (and cookie) and pass it explicitly
      let deviceId = '';
      try {
        const r = await fetch('/api/device-id');
        const j = await r.json();
        deviceId = j.device_id || '';
      } catch (e) {
        console.warn('Unable to get device_id');
      }

      const url = deviceId ? `/api/check-device-session?device_id=${encodeURIComponent(deviceId)}` : '/api/check-device-session';
      const response = await fetch(url);
      const data = await response.json();
      if (data.authenticated) {
        setCurrentUser(data.user);
        setAppMode('feedback');
      } else {
        setAppMode('locationLogin');
      }
    } catch (error) {
      console.error('Error checking session:', error);
      setAppMode('locationLogin');
    }
  };

  const checkExistingAdminSession = async () => {
    try {
      const storedAdmin = localStorage.getItem('adminUser');
      if (storedAdmin) {
        const adminData = JSON.parse(storedAdmin);
        setAdminUser(adminData);
      }
    } catch (error) {
      console.error('Error checking admin session:', error);
      localStorage.removeItem('adminUser');
    }
  };

  // Register service worker for PWA functionality
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }

    // Prevent browser's native PWA install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      console.log('PWA install prompt prevented');
    });

    // Handle fullscreen and address bar hiding on Android Chrome
    const handleFullscreen = async () => {
      // Check if we're on Android Chrome
      const isAndroidChrome = /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isAndroidChrome || isMobile) {
        try {
          // Method 1: Try Fullscreen API
          if (document.documentElement.requestFullscreen) {
            await document.documentElement.requestFullscreen();
          } else if (document.documentElement.webkitRequestFullscreen) {
            document.documentElement.webkitRequestFullscreen();
          } else if (document.documentElement.mozRequestFullScreen) {
            document.documentElement.mozRequestFullScreen();
          } else if (document.documentElement.msRequestFullscreen) {
            document.documentElement.msRequestFullscreen();
          }
        } catch (err) {
          console.log('Fullscreen request failed:', err);
        }

        // Method 2: Scroll to hide address bar (Android Chrome specific)
        if (isAndroidChrome) {
          setTimeout(() => {
            window.scrollTo(0, 1);
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 100);
          }, 500);
        }

        // Method 3: Force viewport height
        const setViewportHeight = () => {
          const vh = window.innerHeight * 0.01;
          document.documentElement.style.setProperty('--vh', `${vh}px`);
        };
        
        setViewportHeight();
        window.addEventListener('resize', setViewportHeight);
        window.addEventListener('orientationchange', setViewportHeight);
      }
    };

    // Try fullscreen on load
    handleFullscreen();

    // Also try on user interaction (required by some browsers)
    const handleUserInteraction = () => {
      handleFullscreen();
      // Remove listener after first interaction
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);

    // Additional method: Hide address bar on scroll
    let lastScrollTop = 0;
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop > lastScrollTop && scrollTop > 100) {
        // Scrolling down - hide address bar
        window.scrollTo(0, scrollTop + 1);
      }
      lastScrollTop = scrollTop;
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  const handleLocationLogin = (user) => {
    setCurrentUser(user);
    setAppMode('feedback');
    
    // Trigger connection status refresh for admin dashboard
    window.dispatchEvent(new CustomEvent('deviceLogin', { detail: { user } }));
    
    // Force refresh to clear cache and load latest code
    setTimeout(() => {
      window.location.reload(true);
    }, 100);
  };

  const handleAdminLogin = (admin) => {
    setAdminUser(admin);
    localStorage.setItem('adminUser', JSON.stringify(admin));
    
    // Force refresh to clear cache and load latest code
    setTimeout(() => {
      window.location.reload(true);
    }, 100);
  };

  const handleAdminLogout = () => {
    setAdminUser(null);
    localStorage.removeItem('adminUser');
  };

  const saveFeedbackAndShowThankYou = async (rating) => {
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating: rating,
          user_id: currentUser?.id || 1
        }),
      });
      
      if (response.ok) {
        console.log('Feedback saved successfully');
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
    
    setCurrentScreen('thankYou');
  };

  const handleEmojiSelection = (rating) => {
    setSelectedRating(rating);
    
    // Check customer rating and route accordingly
    if (rating === 'dissatisfied' || rating === 'very-dissatisfied') {
      setCurrentScreen('contactForm');
    } else if (rating === 'very-satisfied') {
      setCurrentScreen('qrCode');
    } else if (rating === 'neutral') {
      // For neutral rating, save feedback and show thank you
      saveFeedbackAndShowThankYou(rating);
    } else {
      setCurrentScreen('thankYou');
    }
  };

  const handlePhoneSubmit = async (phone, actionType = 'submitted') => {
    setPhoneNumber(phone);
    
    // Save feedback to database
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating: selectedRating,
          phone_number: phone,
          action_type: actionType,
          user_id: currentUser?.id || 1
        }),
      });
      
      if (response.ok) {
        console.log('Feedback saved successfully');
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
    
    setCurrentScreen('thankYou');
  };

  const handleQRCodeSuccess = useCallback(async (actionType = 'submitted') => {
    // Save feedback to database
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating: selectedRating,
          action_type: actionType,
          user_id: currentUser?.id || 1
        }),
      });
      
      if (response.ok) {
        console.log('Feedback saved successfully');
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
    }

    // Log QR review scan
    try {
      const scanResponse = await fetch('/api/qr-review-scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUser?.id || 1,
          rating: selectedRating
        }),
      });
      
      if (scanResponse.ok) {
        console.log('QR review scan logged successfully');
      }
    } catch (error) {
      console.error('Error logging QR review scan:', error);
    }
    
    setCurrentScreen('thankYou');
  }, [selectedRating, currentUser]);

  const handleReturnToFeedback = () => {
    setCurrentScreen('feedback');
    setSelectedRating(null);
    setPhoneNumber(null);
  };

  // Poll server for QR code scans
  useEffect(() => {
    if (currentScreen !== 'qrCode') return;

    const checkForScan = async () => {
      try {
        const response = await fetch(`/api/check-scan?user_id=${currentUser?.id || 1}`);
        const data = await response.json();
        
        if (data.scanned) {
          // QR code was scanned, save feedback and return to main screen
          await handleQRCodeSuccess();
        }
      } catch (error) {
        console.log('Error checking for scan:', error);
      }
    };

    const interval = setInterval(checkForScan, 2000); // Check every 2 seconds
    return () => clearInterval(interval);
  }, [currentScreen, currentUser, handleQRCodeSuccess]);

  // Poll server for session validity every minute
  useEffect(() => {
    // Only poll if we're in feedback mode and have a current user
    if (appMode !== 'feedback' || !currentUser) return;

    const checkSessionValidity = async () => {
      setSessionCheckStatus('checking');
      try {
        const response = await fetch('/api/check-device-session');
        const data = await response.json();
        
        if (!data.authenticated) {
          // Session is invalid, logout and return to login screen
          console.log('Session expired or user logged out, returning to login screen');
          setSessionCheckStatus('expired');
          
          // Trigger connection status refresh for admin dashboard
          window.dispatchEvent(new CustomEvent('deviceLogout', { detail: { user: currentUser } }));
          
          setCurrentUser(null);
          setAppMode('locationLogin');
          setCurrentScreen('feedback');
          setSelectedRating(null);
          setPhoneNumber(null);
        } else {
          setSessionCheckStatus('idle');
        }
      } catch (error) {
        console.error('Error checking session validity:', error);
        // On error, assume session is invalid and logout
        setSessionCheckStatus('expired');
        setCurrentUser(null);
        setAppMode('locationLogin');
        setCurrentScreen('feedback');
        setSelectedRating(null);
        setPhoneNumber(null);
      }
    };

    // Check session validity every minute (60000ms)
    const sessionInterval = setInterval(checkSessionValidity, 60000);
    
    // Also check when window regains focus (user comes back to app)
    const handleFocus = () => {
      if (appMode === 'feedback' && currentUser) {
        checkSessionValidity();
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('visibilitychange', () => {
      if (!document.hidden && appMode === 'feedback' && currentUser) {
        checkSessionValidity();
      }
    });
    
    return () => {
      clearInterval(sessionInterval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('visibilitychange', handleFocus);
    };
  }, [appMode, currentUser]);

  // Render different app modes
  if (appMode === 'loading') {
    return (
      <div className="App loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (appMode === 'locationLogin') {
    return (
      <div className="App">
        <LocationLogin onLoginSuccess={handleLocationLogin} />
        {/* PWA components removed */}
      </div>
    );
  }

  if (appMode === 'mobileLogin') {
    return (
      <div className="App">
        <MobileLogin />
      </div>
    );
  }

  if (appMode === 'admin') {
    return (
      <div className="App admin-mode">
        {adminUser ? (
          <AdminDashboard onLogout={handleAdminLogout} />
        ) : (
          <AdminLogin onLoginSuccess={handleAdminLogin} />
        )}
      </div>
    );
  }

  // Default feedback app mode
  return (
    <div className="App">
      <AnimatePresence mode="wait">
        {currentScreen === 'feedback' && (
          <FeedbackScreen 
            key="feedback"
            onEmojiSelect={handleEmojiSelection}
          />
        )}
        {currentScreen === 'contactForm' && (
          <ContactFormScreen 
            key="contactForm"
            selectedRating={selectedRating}
            onSubmit={handlePhoneSubmit}
          />
        )}
        {currentScreen === 'qrCode' && (
          <QRCodeScreen 
            key="qrCode"
            onSubmit={handleQRCodeSuccess}
            userId={currentUser?.id || 1}
            reviewUrl={currentUser?.review_url}
          />
        )}
        {currentScreen === 'thankYou' && (
          <ThankYouScreen 
            key="thankYou"
            onReturn={handleReturnToFeedback}
            selectedRating={selectedRating}
            phoneNumber={phoneNumber}
          />
        )}
      </AnimatePresence>
      
      {/* Show current location info */}
      {currentUser && (
        <div className="location-info">
          📍 {currentUser.location_name}
          {sessionCheckStatus === 'checking' && (
            <span className="session-status checking">🔄 Checking session...</span>
          )}
          {sessionCheckStatus === 'expired' && (
            <span className="session-status expired">⚠️ Session expired</span>
          )}
        </div>
      )}
      
      {/* PWA components removed */}
      <FullscreenButton />
    </div>
  );
}

export default App;
